import { Component, Input, OnInit, inject } from '@angular/core'
import { EnvDirective } from '@shared/env.directive'
import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { TbMenuComponent } from '@components-library/tb-menu'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { TbTooltipComponent } from '@components-library/tb-tooltip/tb-tooltip-component/tb-tooltip.component'
import { TranslocoDirective } from '@jsverse/transloco'
import {
    Automation,
    AutomationCreateModel,
    AutomationDeleteModel,
    AutomationExecuteModel,
    AutomationUpdateModel,
} from '@models/ui/automation.model'
import {
    AutomationFacadeService,
    ApplicationFacadeService,
    SchemaFacadeService,
    FieldFacadeService,
} from '@services/store-facade'
import { AutomationTypes } from '@models/response/automation.model'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { BaseMainBusinessRecord } from '@models/ui'
import { FieldTypes } from '@core/models'
import { RecordActionPanelService } from '@app/feature/record-actions-panel/record-action-panel.service'
import { finalize, combineLatest } from 'rxjs'
import { FieldService } from '@services/entity-services/field/field.service'

@UntilDestroy()
@Component({
    selector: 'app-automation-buttons',
    imports: [
        EnvDirective,
        NgTemplateOutlet,
        TbMenuComponent,
        AsyncPipe,
        TbButtonComponent,
        TbIconComponent,
        TbTooltipComponent,
        TranslocoDirective,
    ],
    templateUrl: './automation-buttons.component.html',
})
export class AutomationButtonsComponent implements OnInit {
    private readonly automationFacadeService = inject(AutomationFacadeService)
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly applicationFacadeService = inject(ApplicationFacadeService)
    private readonly actionPanelService = inject(RecordActionPanelService)
    private readonly fieldSearchService = inject(FieldService)
    private readonly fieldFacadeService = inject(FieldFacadeService)

    @Input() selectedRecords!: BaseMainBusinessRecord[]

    @Input() dark = false

    @Input() displayTooltip = false

    schemaGuid!: string
    templateAutomatedFieldsGuid!: string

    automations$ = this.automationFacadeService.selectSelectedTableSchemaAutomations$

    executableAutomations$ =
        this.automationFacadeService.selectSelectedTableSchemaAutomationsByType$(
            AutomationTypes.MANUAL,
        )

    ngOnInit() {
        combineLatest([
            this.schemaFacadeService.selectSelectedTableSchema$,
            this.fieldFacadeService.selectSelectedSchemaFieldEntities$,
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([schema, fieldEntities]) => {
                if (!schema || !fieldEntities) return

                // TODO: delete. temporary solution for automations
                this.schemaGuid = schema.guid
                this.templateAutomatedFieldsGuid = this.fieldSearchService.findFieldByType(
                    fieldEntities,
                    FieldTypes.NAME,
                )!.guid
            })
    }

    // no design, final solution will be implemented later, for testing purposes
    createAutomation() {
        const createModel: AutomationCreateModel = {
            name: 'test',
            solution_object_type_guid: this.schemaGuid,
            conditions: [
                {
                    source_type: 'source_type_event',
                    source_details: 'create',
                    values: [],
                },
            ],
            actions: [
                {
                    action_type_code: 'action_item',
                    action_config: {
                        target_object_field_guid: this.templateAutomatedFieldsGuid,
                        value: 'automation test data',
                    },
                },
            ],
            automation_type: AutomationTypes.MANUAL,
            is_active: 0,
            user_guids: [],
            is_global: 0,
        }

        this.automationFacadeService.createAutomation(createModel).subscribe()
        this.clearAllSelected()
    }

    updateAutomation(automation: Automation) {
        const updateModel: AutomationUpdateModel = {
            automation_guid: automation.guid,
            automation_model: {
                name: automation.name + '1',
                solution_object_type_guid: this.schemaGuid,
                conditions: [
                    {
                        source_type: 'source_type_event',
                        source_details: 'create',
                        values: [],
                    },
                ],
                actions: [
                    {
                        action_type_code: 'action_item',
                        action_config: {
                            target_object_field_guid: this.templateAutomatedFieldsGuid,
                            value: 'automation test data',
                        },
                    },
                ],
                automation_type: AutomationTypes.AUTO,
                is_active: 0,
                user_guids: [],
                is_global: 0,
            },
        }

        this.automationFacadeService.updateAutomation(updateModel).subscribe()
        this.clearAllSelected()
    }

    deleteAutomation(guid: string) {
        const deleteModel: AutomationDeleteModel = {
            guid: guid,
            object_type_guid: this.schemaGuid,
        }

        this.automationFacadeService.deleteAutomation(deleteModel).subscribe()
        this.clearAllSelected()
    }

    executeAutomation(guid: string) {
        const executeModel: AutomationExecuteModel = {
            guid,
            object_type_guid: this.schemaGuid,
            records: this.selectedRecords,
        }

        this.applicationFacadeService.setLoading(true)
        this.automationFacadeService
            .executeAutomation(executeModel)
            .pipe(finalize(() => this.applicationFacadeService.setLoading(false)))
            .subscribe()
        this.clearAllSelected()
    }

    private clearAllSelected() {
        this.actionPanelService.deselectAll()
    }
}
